import { TextField } from '@mui/material';
import Button from '@mui/material/Button';
import GridReal from '@mui/material/Grid/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';

import * as authService from '~/services/authentication';
import { SystemService } from '~/services/system';
import { AuthStatus, Roles } from '~/types/types';

const Grid = styled(GridReal)({
  padding: '8px',
});

const ImportLogList = () => {
  const [imports, setImports] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [noMorePages, setNoMorePages] = useState(false);

  useEffect(() => {
    const subscription = SystemService.getImportLog(page).subscribe((data: any) => {
      if (data.length < 20) {
        setNoMorePages(true);
      }
      data = data.map((i: any) => {
        i.created = i.created.split('.')[0].replace('T', ' ');
        //time formatting needed for legacy import log
        const [text, time] = i.status.split('Tid:');
        if (!time.includes('min') && !time.includes('sek')) {
          const d = Math.round(time.replace('s', ''));
          i.status = `${text} Tid: ${Math.round((d % 3600) / 60)} min ${Math.round((d % 3600) % 60)} sek`;
        }
        return i;
      });
      setImports((prevState) => {
        return [...prevState, ...data];
      });
    });

    return () => subscription.unsubscribe();
  }, [page]);

  const fetchMore = () => {
    setPage((page) => page + 1);
  };

  return (
    <>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Datum</TableCell>
            <TableCell>Användarnamn</TableCell>
            <TableCell>Version</TableCell>
            <TableCell>Filidentitet</TableCell>
            <TableCell>Meddelande</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {imports.map((importJob: any, index) => (
            <TableRow key={index}>
              <TableCell>{importJob.created}</TableCell>
              <TableCell>{importJob.principalname}</TableCell>
              <TableCell>{importJob.version}</TableCell>
              <TableCell>{importJob.fileId}</TableCell>
              <TableCell>{importJob.status}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!noMorePages && (
        <Button variant="text" onClick={fetchMore}>
          Hämta fler loggar
        </Button>
      )}
    </>
  );
};

enum SystemMessageMode {
  View = 0,
  Edit = 1,
}

export const UserAdminRoles: Readonly<Roles[]> = [Roles.Administrator];

const useAuthorized = (roles: Readonly<Roles[]>) => {
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const subscription = authService.getAuthStatusObs(roles).subscribe((authStatus: AuthStatus) => {
      setAuthorized(authStatus == AuthStatus.Authorized);
    });

    return () => subscription.unsubscribe();
  }, []);

  return authorized;
};

const SystemMessage = () => {
  const [mode, setMode] = useState(SystemMessageMode.View);
  const [message, setMessage] = useState('');
  const [tempMessage, setTempMessage] = useState('');
  const authorized = useAuthorized(UserAdminRoles);

  useEffect(() => {
    const subscription = SystemService.getSystemMessage().subscribe((message: string) => {
      setMode(SystemMessageMode.View);
      setMessage(message);
    });

    return () => subscription.unsubscribe();
  }, []);

  const changeMessage = () => (event: React.ChangeEvent<HTMLInputElement>) => {
    setTempMessage(event.target.value);
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    SystemService.setSystemMessage(tempMessage);
  };

  const switchMode = () => {
    if (mode === SystemMessageMode.View) {
      setTempMessage(message);
      setMode(SystemMessageMode.Edit);
    } else {
      setMode(SystemMessageMode.View);
    }
  };

  if (mode === SystemMessageMode.View) {
    return (
      <Typography color="primary">
        {message}
        {authorized && (
          <>
            &nbsp;
            <Button variant="text" onClick={switchMode}>
              Ändra
            </Button>
          </>
        )}
      </Typography>
    );
  } else {
    return (
      <form onSubmit={onSubmit}>
        <TextField
          variant="standard"
          fullWidth
          margin="none"
          color="primary"
          label="Meddelande"
          value={tempMessage}
          onChange={changeMessage()}
        />
        <Button type="submit">OK</Button>
        <Button onClick={() => setMode(SystemMessageMode.View)}>Avbryt</Button>
      </form>
    );
  }
};

export const Dashboard = (props: any) => {
  return (
    <>
      <Grid container item xs={12} lg={10} spacing={2}>
        <Grid item>
          <Typography variant="h4">Välkommen till TPS</Typography>
          <Typography>Tryck på knappen uppe till vänster för att se menyn.</Typography>
        </Grid>
      </Grid>

      <Grid container item xs={12} lg={10} spacing={2}>
        <Grid item>
          <SystemMessage />
        </Grid>
      </Grid>

      <Grid container item xs={12} lg={10} spacing={2}>
        <Grid item>
          <Typography variant="h6">Senaste importerna</Typography>
          <ImportLogList />
        </Grid>
      </Grid>
    </>
  );
};
